// =============================================================================
// Moteur d'interpolation de templates emails (style mustache léger)
//
// Supporte :
//   - tokens simples           : {{prenom}}, {{salarie.prenom}}, {{platform.url}}
//   - sections (array / bool)  : {{#missing_documents}}<li>{{this}}</li>{{/...}}
//   - sections inversées       : {{^missing_documents}}…{{/missing_documents}}
//
// Volontairement minimal — pas de dépendance externe (compatible navigateur).
// =============================================================================

export type TemplateValue = string | number | boolean | null | undefined | string[];
export type TemplateContext = Record<string, TemplateValue>;

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Remplace les tokens d'un template par les valeurs du contexte.
 * Les valeurs `string[]` alimentent les sections ; les chaînes simples sont
 * injectées telles quelles (le HTML des templates est de confiance — édité par
 * les admins Aon — donc pas d'échappement systématique du corps).
 */
export function interpolateTemplate(input: string, ctx: TemplateContext): string {
  let out = input;

  // 1) Sections normales {{#key}}...{{/key}}
  out = out.replace(
    /\{\{#\s*([\w.]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/g,
    (_match, key: string, inner: string) => {
      const val = ctx[key];
      if (Array.isArray(val)) {
        if (val.length === 0) return "";
        return val.map((item) => inner.replace(/\{\{\s*\.?this\s*\}\}/g, String(item))).join("");
      }
      return val ? inner : "";
    },
  );

  // 2) Sections inversées {{^key}}...{{/key}}
  out = out.replace(
    /\{\{\^\s*([\w.]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/g,
    (_match, key: string, inner: string) => {
      const val = ctx[key];
      const empty = Array.isArray(val) ? val.length === 0 : !val;
      return empty ? inner : "";
    },
  );

  // 3) Tokens simples {{key}}
  out = out.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) => {
    const val = ctx[key];
    if (val == null || Array.isArray(val) || typeof val === "boolean") return "";
    return String(val);
  });

  return out;
}

/** Renvoie la liste des tokens (hors sections/this) présents dans un template. */
export function extractTemplateTokens(input: string): string[] {
  const tokens = new Set<string>();
  const re = /\{\{\s*[#^/]?\s*([\w.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input))) {
    const tok = m[1];
    if (tok && tok !== "this") tokens.add(tok);
  }
  return Array.from(tokens);
}
